import cn from "classnames";
import { unwrapResult } from "@reduxjs/toolkit";
import { shallowEqual } from "react-redux";
import { ErrorBoundary } from "react-error-boundary";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import React, { useEffect, useState, useRef } from "react";

// Mui Components
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import { Box, Button, Collapse, Typography, IconButton, Grid, Link, Skeleton, Alert } from "@mui/material";

// Styles
import peopleStyles from "./People.styles";

// Components
import CustomIcon from "components/CustomIcon";
import TalentTable from "components/Talent/TalentTable";
import TalentTableToolbar from "components/Talent/TalentTable/TalentTableToolbar";
import { useAutocompleteFunctionsLevels, useAutocompletePeople } from "components/Autocomplete/AutocompleteField";
import ChipsArray from "components/Domain/ChipsArray";
import SelectAndChips from "./SelectAndChips";
import CountryCity from "../CountryCity";
import WHFilter, { WHData } from "components/Talent/WHFilter";
import NavButton from "components/HeaderNavigation/NavButton";
import { TalentHeader, TalentBody } from "../TalentLayoutContent";
import GenericError from "components/Errors/GenericError";
import JobTitle from "../JobTitle";
import { initNarrative } from "./LSPMFilter/LSPMFilter";
import TitleFilter from "../Companies/TitleFilter";
import TablePagination from "../TablePagination";
import CustomAutocomplete from "components/Autocomplete/CustomAutocomplete";
import { useSnackBar, SnackBar } from "components/SnackBar";
import { ChartErrorMessage } from "components/ChartErrorMessage";
import { Balance, TeamCompetitivePositioning } from "components/Charts";
import { InputSlider } from "components/InputSlider";
import useResizeObserver from "use-resize-observer";
import { BriefFacets } from "pages/Brief/components/BriefFacets";
import { CardMessageWrapper, CardMessage } from "components/Message";
import Modal from "components/Modal";
import { mapForChipEntity } from "components/Domain/DomainLayout/DomainLayout.functions";
import { ClearFilterButton } from "components/Buttons";

// Hooks
import { useIntl } from "react-intl";
import useBriefProfile from "./useBriefProfile";
import { useAppSelector, useAppDispatch } from "app/hooks";
import useTableActions from "components/Talent/TalentTable/useTableActions";

// Utils
import { urlPaths } from "enums/urlPaths";
import { difference, get, isEmpty, isEqual, orderBy } from "lodash";
import { extractFacetData, snakeCaseToCapitalizedString, sortWorkHistory, useRedirectOnProjectError } from "utils";
import {
    getNestedSortQueryParams,
    getPersonProp,
    getPeopleParams,
    talentPotentialMembersPayloadColumns,
    requestPeopleData,
} from "services/people/people.functions";
import {
    getInitialFetchResponse,
    getInsightRelatedSltPath,
    getListsFetchResponse,
    getInsightFilters,
    getSearchAiCompaniesLonglist,
    getSearchAiLeadersFilters,
    getSearchAiLeadersLongList,
    getSearchAiLeadersShortList,
    TInitialFetch,
    TInitialFetchError,
} from "pages/ProjectDashboard/adapters/formattedData";

// Services
import { getPeopleService } from "services/people/people.service";

// Store
import { IFilterPeople } from "store/slice/currentSearch";
import { clearLongList, clearRefinementData, clearShortList } from "store/slice/refinement";
import { getLD4, getLD5 } from "store/slice/charts";
import { hideLoader, getLoaderState, toggleLoader } from "store/slice/UI";
import { fetchCompanySet } from "store/slice/Project/companiesSet/companiesSetSlice";
import { getRoles } from "store/slice/Team/roles/rolesSlice";
import { getInsightById, getProjectInsight } from "store/slice/Project/insights/insightsSlice";
import {
    addPersonSet,
    fetchPersonSet,
    updatePersonSet,
    getPersonSetByPath,
} from "store/slice/Project/peopleSet/peopleSetSlice";
import {
    getSearchAiById,
    fetchProjectSearchAi,
    updateSearchAiChild,
} from "store/slice/Project/searchAis/searchAisSlice";

// Others
import icons from "enums/icons";

// Constant
import { initialPaginationOS } from "constants/constants";
import { WHdata, WHRelevance } from "components/Talent//WHFilter";
import { defaultLocationData, defaultCityRadius, defaultCountryData } from "../constants/talent.constants";

// Types
import {
    ILocationSearch,
    ILocationCountry,
    ILocationCountryById,
    ILocationDataById,
    fetchCountryByIdService,
    fetchLocationByIdService,
    saveProjectByPathService,
    updateProjectByPathService,
} from "services";
import { PersonCustomAutocomplete, PersonTalent, WorkHistoryData } from "services/people/people.types";
import { TAutocompleteFieldSelectedObjectValue } from "components/Autocomplete/AutocompleteField/AutocompleteField.types";
import {
    PeoplePayload,
    ColumnsPeople,
    TshowPopoverPeople,
    apiSortIdValues,
} from "components/Talent/TalentTable/TalentTable.types";
import { Order, OrderTable } from "components/SortMenu/SortMenu";
import { ChipEntity } from "types";
import { TLSPMNarrativeBase } from "./LSPMFilter/LSPMFilter.types";
import { ProjectCompanySet, ProjectPersonSet, ProjectSearchAICreationResponse } from "services/projects/project.types";
import { getFunctionsAndLevelsThunk } from "store/slice/Project/functions/functionsSlice";
import { TElement } from "components/Autocomplete/AutocompleteField/hooks/useAutocompleteFunctionsLevels";

type Pagination = {
    page: number;
    rows: number;
    orderByColumn?: string;
};

export const initPersonInfo = { left: -1, top: -1, id: -1 };

export const convertFiltersForProjects = (filters: IFilterPeople) => {
    const selectedWHScope = filters.workHistory.find((wh) => wh.selected) as WHData;
    const WHScope = selectedWHScope?.label.split(".");

    return {
        work_history_scope: selectedWHScope ? WHScope[WHScope.length - 1].toUpperCase() : "ALL",
        role_functions: filters.selectedFunctions.map((role) => role.value),
        role_levels: filters.selectedHierarchies.map((level) => level.value),
        job_title: filters.jobTitleTerm,
        functional_balance: filters.briefData.chart1,
        individual_positioning: filters.briefData.chart2,
        facet_situational: { narratives: extractFacetData(filters.briefData.facets.situational) },
        facet_domain: { narratives: extractFacetData(filters.briefData.facets.domain) },
        facet_functional: { narratives: extractFacetData(filters.briefData.facets.functional) },
        geo_radius: {
            country_id: filters.selectedCountry.id,
            city_id: filters.selectedCity.id,
            radius: Number(filters.radius),
            location: {
                lat: filters.selectedCity.location?.latitude || null,
                lon: filters.selectedCity.location?.longitude || null,
            },
        },
        lspm_scores: filters.lspmNarratives
            .map((lspm) => {
                return {
                    narrative: lspm.id.toUpperCase(),
                    score_range: {
                        min: lspm?.checkboxes?.minimum ? lspm.min ?? null : null,
                        max: lspm?.checkboxes?.maximum ? lspm.max ?? null : null,
                    },
                };
            })
            .filter((lspm) => lspm.narrative.length),
    };
};

const People = () => {
    const intl = useIntl();
    const classes = peopleStyles();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();

    const { companyId: companyIdParams } = useParams();
    const projectId = searchParams.get("projectId");
    const briefId = searchParams.get("briefId");
    const insightId = searchParams.get("insightId");
    const searchAiId = searchParams.get("searchAiId");

    // Redirect to 404 if invalid project id or not enough permission
    useRedirectOnProjectError([projectId, insightId, searchAiId]);

    const [isDumbSearch, setIsDumbSearch] = useState<boolean>(true);
    const [activeButton, setActiveButton] = useState<number>(0);

    const [collapse, setCollapse] = useState(true);
    const [hasBoardMembers, setHasBoardMembers] = useState(true);
    const [collapseCompanies, setCollapseCompanies] = useState(true);
    const [showCollapseExpand, setShowCollapseExpand] = useState(false);
    const [showPopover, setShowPopover] = useState<TshowPopoverPeople>("none");

    const containerRef = useRef<HTMLDivElement | null>(null);

    // Data
    const isLoaderVisible = useAppSelector(getLoaderState);
    const { data: rolesData } = useAppSelector(getRoles);
    const { data: insightData } = useAppSelector(getInsightById(Number(insightId)), shallowEqual);
    const { data: searchAiData } = useAppSelector(getSearchAiById(Number(searchAiId)), shallowEqual);
    const [shouldFetchBrief, setShouldFetchBrief] = useState(false);

    const { shouldOpenSnackBar, popOverAnchorEl, handleOpenSnackBar, handleCloseSnackBar } = useSnackBar();
    const snackBarWrapperRef = useRef<HTMLElement | null | undefined>(null);
    const { ref: briefChartsWrapperRef, width = -1, height = -1 } = useResizeObserver<HTMLDivElement>();

    const [peopleLonglistPath, setPeopleLonglistPath] = useState("");
    const peopleLonglistData = useAppSelector(getPersonSetByPath(peopleLonglistPath), shallowEqual);

    const companyId = companyIdParams || getInsightFilters(insightData)?.data.company.id || undefined;

    // Selected companies
    const [selectedCompanies, setSelectedCompanies] = useState<TAutocompleteFieldSelectedObjectValue[]>([]);

    // Functions & Hierarchies
    const wereFHUpdated = useRef(false);

    const {
        functions: functionsList,
        hierarchies: hierarchiesList,
        placeholderFunctions,
        placeholderHierarchy,
        status: functionsAndHierarchiesStatus,
    } = useAutocompleteFunctionsLevels();

    const [functions, setFunctions] = useState<ChipEntity[]>([]);
    const [hierarchies, setHierarchies] = useState<ChipEntity[]>([]);

    // Job titles
    const [jobTitleTerm, setJobTitleTerm] = useState("");

    // Cities and countries
    const [city, setCity] = useState<ILocationSearch>(defaultLocationData);
    const [radius, setRadius] = useState<string | number>(defaultCityRadius);
    const [country, setCountry] = useState<ILocationCountry>(defaultCountryData);

    // WH Filter
    const [whFilter, setWHFilter] = useState<WHData[]>(WHdata);

    // WH Relevance Filter
    const [whRelevanceFilter, setWHRelevanceFilter] = useState<WHData[]>(WHRelevance);

    const isFiltersModified =
        !isEqual(whFilter, WHdata) ||
        !isEqual(whRelevanceFilter, WHRelevance) ||
        jobTitleTerm !== "" ||
        !isEqual(city, defaultLocationData) ||
        !isEqual(country, defaultCountryData);

    // LSPM Filter
    const [narrativesArr, setNarrativesArr] = useState<TLSPMNarrativeBase[]>([initNarrative]);

    // Popover Person
    const personInfo = useRef({ ...initPersonInfo });

    // Table
    const [people, setPeople] = useState<PeoplePayload>({
        meta: initialPaginationOS,
        results: [],
    });
    const [selectedShortList, setSelectedShortList] = useState<PersonTalent[]>([]);
    const [selectedPeople, setSelectedPeople] = useState<PersonTalent[]>([]);
    const [sortingDirection, setSortingDirection] = useState<OrderTable>("default");

    const [pagination, setPagination] = useState<Pagination>({ page: 0, rows: 100 });
    const [pageValue, setPageValue] = useState<number | string>(0);
    const previousPageValue = useRef<number | string>(1);
    const paginationTextInputRef = useRef<any>(null);

    // Brief
    const {
        briefData,
        briefDataStore,
        chartStatus,
        wasBriefModified,
        showBriefError,
        showConfirmBriefChanges,
        briefChangesConfirmed,
        setShowBriefError,
        handleRevertChanges,
        handleSetBriefData,
        handleUpdateBriefData,
        handleConfirmChanges,
        handleShowConfirmationModal,
    } = useBriefProfile({
        shouldFetchBrief,
        functions,
        setFunctions,
        hierarchies,
        setHierarchies,
        functionsList,
        hierarchiesList,
    });

    const [
        { markedLeft, markedRight },
        {
            setMarkedLeft,
            setMarkedRight,
            handleAddBulk,
            handleDeleteBulk,
            handleClickRow,
            handleAddSelectAllClick,
            handleDeleteSelectAllClick,
            handleAddRow,
            handleDeleteRow,
        },
    ] = useTableActions({ data: people.results, setSelectedPeople, type: "people", selectedPeople });

    const {
        peopleList,
        suggestedPeopleLists,
        placeholderPerson,
        currentPage,
        totalPages,
        pageSize,
        resultsRange,
        showPreviousSearchBtn,
        searchRequestStatus,
        shouldShowLoadingState,
        setShowPreviousSearchBtn,
        handlePageChange,
        setPeopleList,
        setSuggestedPeopleLists,
        getOptionLabelPerson,
        handleInputChangePerson,
        resetStates,
        handleListsOrder,
        handleBackToPreviousResults,
        handleFetchSuggestedList,
        handleUpdateSuggestedList,
    } = useAutocompletePeople();

    const handlePersonSelect = (
        event: React.SyntheticEvent,
        person: Pick<PersonCustomAutocomplete, "person_id" | "name"> | null
    ) => {
        if (person === null) return;

        const personFound = selectedPeople.find(({ id }) => id === (person as PersonTalent).id);

        if (!personFound) {
            setSelectedPeople((currentPeople) => [person as PersonTalent, ...currentPeople]);
        }
    };

    const handleTabChange = (index: number) => {
        if (index !== activeButton) {
            setIsDumbSearch(!isDumbSearch);
            setActiveButton(index);
        }
    };

    const convertSortValue = (orderByColumn: string) => {
        const sort = orderByColumn.split(",");

        if (sort[0].includes("work_history")) {
            return getNestedSortQueryParams(sort[0], sort[1], selectedCompanies);
        }

        if (sort[0].includes("score")) {
            return {
                _score: { order: "desc" },
                "name.keyword_lc": { order: "asc" },
            };
        }

        return { [sort[0]]: { order: sort[1] } };
    };

    const convertOrderValue = (orderByColumn: string) => {
        const order = orderByColumn.split(",");
        return {
            field: order[0],
            direction: order[1],
        };
    };

    const sortByWorkHistoryProp = (peopleList: PersonTalent[], sortBy: string, field: keyof WorkHistoryData) => {
        const direction = sortBy.split(",")[1];
        return orderBy(
            peopleList,
            [
                function (person) {
                    return ((person.work_history[0] as WorkHistoryData)[field] as string).toLowerCase();
                },
            ],
            direction === "asc" ? ["asc", "desc"] : ["desc", "asc"]
        );
    };

    const reorderWorkHistory = (
        peopleList: PersonTalent[],
        paginationOptions?: { page: number; rows?: number; orderByColumn?: string }
    ) => {
        peopleList.forEach((person) => {
            const companies = selectedCompanies.map((company) => company.value);
            const sortedWorkHistory = sortWorkHistory(person.work_history, "tenure_to");

            const workHistoryWithSelectedCompany = sortedWorkHistory.reduce((acc, workHistory) => {
                const selectedWH = companies.includes((workHistory.company_name || workHistory.unlinked_company) ?? "")
                    ? workHistory
                    : ({} as WorkHistoryData);

                if (isEmpty(selectedWH)) {
                    return acc;
                } else {
                    return selectedWH.is_current_employment ? [selectedWH] : [...acc, selectedWH];
                }
            }, [] as WorkHistoryData[]);

            person.work_history = workHistoryWithSelectedCompany;
        });

        if (paginationOptions?.orderByColumn?.includes("company_name")) {
            return sortByWorkHistoryProp(peopleList, paginationOptions.orderByColumn, "company_name");
        }

        if (paginationOptions?.orderByColumn?.includes("drx_work_position_label")) {
            return sortByWorkHistoryProp(peopleList, paginationOptions.orderByColumn, "drx_work_position_label");
        }

        return peopleList;
    };

    const handlePeopleFetch = async (
        paginationOptions?: { page: number; rows?: number; orderByColumn?: string },
        dontShowLoader?: boolean
    ) => {
        try {
            !dontShowLoader && dispatch(toggleLoader(true));

            // We can't use order_by and sort at the same time. Brief data is passed as an order_by prop.
            // We need to use briefs if it's an advanced search and user sorts by "Best Match" (orderByColumn === "_score,desc")
            const shouldUseSort = isDumbSearch;
            const isSortBestMatch =
                paginationOptions?.orderByColumn === undefined || paginationOptions?.orderByColumn === "_score,desc";
            const shouldUseBriefData = !isDumbSearch && !isEmpty(briefData) && isSortBestMatch;

            const commonParams = {
                jobTitleTerm: jobTitleTerm,
                companies: selectedCompanies.filter((company) => company.selected).map(({ key }) => key),
                meta: {
                    page_no: paginationOptions?.page ?? 1,
                    page_size: paginationOptions?.rows || pagination.rows,
                },
                ...(shouldUseSort || !isSortBestMatch
                    ? {
                          sort: {
                              ...(paginationOptions?.orderByColumn
                                  ? { ...convertSortValue(paginationOptions.orderByColumn) }
                                  : { _score: { order: "desc" }, "name.keyword": { order: "asc" } }),
                          },
                      }
                    : {}),
            };

            const params = getPeopleParams(
                isDumbSearch
                    ? {
                          ...commonParams,
                          workHistoryState: whRelevanceFilter.find((e) => e.selected)?.label,
                      }
                    : {
                          ...commonParams,
                          workHistoryState: whFilter.find((e) => e.selected)?.label,
                          functions: functions.map((f) => f.value.toLowerCase()),
                          hierarchies: hierarchies.length ? hierarchies.map((h) => parseInt(h.value)) : undefined,
                          location: {
                              ...(country.id && !city.id ? { countryId: country.id } : {}),
                              ...(city.id ? { city, location_radius: Number(radius) } : {}),
                          },
                          lspmNarratives: narrativesArr,
                          ...(isSortBestMatch
                              ? {
                                    order_by: [
                                        ...(shouldUseBriefData
                                            ? [
                                                  {
                                                      operator: "SCORE_LSPM_CHARTS_HITS",
                                                      operand: {
                                                          chart_bubbles: {
                                                              functional_balance: {
                                                                  x: briefData?.chart1.x,
                                                                  y: briefData?.chart1.y,
                                                                  r: briefData?.chart1.radius,
                                                              },
                                                              individual_positioning: {
                                                                  x: briefData?.chart2.x,
                                                                  y: briefData?.chart2.y,
                                                                  r: briefData?.chart2.radius,
                                                              },
                                                          },
                                                          facets: {
                                                              situation_narratives: briefData?.facets.situational.map(
                                                                  (facet) => ({
                                                                      field: facet.id.toLowerCase(),
                                                                      weight: facet.weight,
                                                                  })
                                                              ),
                                                              domain_narratives: briefData?.facets.domain.map(
                                                                  (facet) => {
                                                                      return {
                                                                          field: facet.id
                                                                              .toLowerCase()
                                                                              .replace(
                                                                                  "situation_value_crystallisation",
                                                                                  "domain"
                                                                              ),
                                                                          weight: facet.weight,
                                                                      };
                                                                  }
                                                              ),
                                                          },
                                                      },
                                                      direction: "asc",
                                                  },
                                              ]
                                            : []),
                                        ...(!shouldUseBriefData && paginationOptions?.orderByColumn
                                            ? [
                                                  { ...convertOrderValue(paginationOptions.orderByColumn) },
                                                  {
                                                      field: "id",
                                                      direction: "asc",
                                                  },
                                              ]
                                            : []),
                                    ],
                                }
                              : {}),
                      }
            );

            const { data: dataResults } = await getPeopleService<PersonTalent>(params);

            setPeople({
                meta: {
                    results_total: dataResults.meta.hits,
                    page_total: dataResults.meta.pages,
                    page_size: dataResults.meta.page_size,
                    page: dataResults.meta.page_no,
                },
                results: isDumbSearch
                    ? reorderWorkHistory(dataResults.results, paginationOptions)
                    : dataResults.results,
            });

            setPagination((currentPagination) => ({ ...currentPagination, ...paginationOptions }));
            if (pageValue === "" || Number(pageValue) === 0) {
                setPageValue(1);
            }
        } catch (error) {
            console.error(error);
        } finally {
            !dontShowLoader && dispatch(hideLoader(false));
        }
    };

    const onRequestSort = async (orderByColumn: apiSortIdValues, direction: OrderTable, propertyId: string) => {
        const orderBy = direction === "default" ? "_score,desc" : `${orderByColumn},${direction as Order}`;
        setPagination((currentPagination) => ({ ...currentPagination, orderByColumn: orderBy }));
        handlePeopleFetch(
            {
                page: pagination.page ?? 1,
                rows: pagination.rows,
                orderByColumn: orderBy,
            },
            true
        );
    };

    const onSort = (property: ColumnsPeople, direction: Order, isNumeric: boolean) => {
        if (!selectedPeople.length) return;

        if (isNumeric) {
            const sortedPeople = [...selectedPeople].sort((personA, personB) => {
                const a = Number(get(personA, property));
                const b = Number(get(personB, property));
                return direction === "desc" ? a - b : b - a;
            });
            setSelectedPeople(sortedPeople);
        } else {
            const sortedPeople = [...selectedPeople].sort((personA, personB) => {
                const [path, prop] = property.split(".");
                const peopleArray = get(personA, path);
                let a, b;

                if (Array.isArray(peopleArray) && property.includes("work_history")) {
                    a = String(getPersonProp(personA, path, prop)).toLowerCase();
                    b = String(getPersonProp(personB, path, prop)).toLowerCase();
                } else {
                    a = String(get(personA, property) ?? "").toLowerCase();
                    b = String(get(personB, property) ?? "").toLowerCase();
                }
                if (a === b) return 0;
                else {
                    if (direction === "asc") return a < b ? -1 : 1;
                    else return a < b ? 1 : -1;
                }
            });
            setSelectedPeople(sortedPeople);
        }
    };

    const updateSearch = async () => {
        try {
            dispatch(toggleLoader(true));
            const { data: searchAiFilters } = await saveProjectByPathService({
                id: Number(projectId),
                project_path: `/project_${projectId}/search_ai_list/search_ai_${searchAiId}/search_leaders`,
                data: convertFiltersForProjects({
                    radius,
                    jobTitleTerm: jobTitleTerm,
                    selectedFunctions: functions,
                    selectedHierarchies: hierarchies,
                    selectedRoles: [], //roles,
                    selectedCity: city,
                    selectedCountry: country,
                    workHistory: whFilter,
                    workHistoryRelevance: whRelevanceFilter,
                    lspmNarratives: narrativesArr,
                    briefData: briefData,
                }),
                echo: true,
            });

            if (!isEmpty(searchAiFilters)) {
                dispatch(
                    updateSearchAiChild({
                        data: searchAiFilters,
                        id: Number(searchAiId),
                        type: "PROJECT_SEARCH_LEADERS",
                    })
                );
            }
        } catch (error) {
            console.error(error);
            dispatch(toggleLoader(false));
            return;
        }
    };

    const handleGoBack = () => {
        navigate(
            `${urlPaths.Talent}/${companyId}?projectId=${projectId}&insightId=${insightId}&briefId=${briefId}&searchAiId=${searchAiId}`
        );
    };

    const handleSaveLeaders = async () => {
        if (projectId === null) return;

        if (wasBriefModified && !briefChangesConfirmed && !isDumbSearch) {
            setShowBriefError(true);
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
            return;
        }

        dispatch(toggleLoader(true));

        const peopleSetPath = getSearchAiLeadersLongList(searchAiData)?.ref?.path || "";

        if (peopleSetPath) {
            // Updating
            const peopleList: ProjectPersonSet = unwrapResult(
                await dispatch(fetchPersonSet({ projectId, path: peopleSetPath }))
            );
            const peopleMetaUiList = peopleList?.meta_ui?.person_ids || [];

            const longList = selectedPeople.map((longListPerson) => {
                const memberUI = peopleMetaUiList.find((e) => e.id === Number(longListPerson.id));
                return {
                    id: longListPerson.id,
                    statusUI: "enabled" as const,
                    ...(memberUI?.role_id
                        ? {
                              roleKey: `${memberUI.role_id}`,
                              role: rolesData.find((r) => r.id === Number(memberUI.role_id))?.name,
                          }
                        : {}),
                };
            });

            const notInterestedList = peopleMetaUiList
                .filter((memberMetaUI) => memberMetaUI.hasOwnProperty("in"))
                .map((memberMetaUI) => {
                    const member = selectedShortList.find((e) => e.id === Number(memberMetaUI.id));
                    return {
                        ...member,
                        id: Number(member?.id),
                        person_id: Number(member?.id),
                        in: memberMetaUI?.in,
                        statusUI: "enabled" as const,
                        ...(memberMetaUI?.role_id
                            ? {
                                  roleKey: `${memberMetaUI.role_id}`,
                                  role: rolesData.find((r) => r.id === Number(memberMetaUI.role_id))?.name,
                              }
                            : {}),
                    };
                });

            const personIds = [...longList, ...notInterestedList].map((member) => ({
                id: member.id,
                ...(member.roleKey ? { role_id: Number(member.roleKey) } : {}),
                ...(member.hasOwnProperty("in") ? { in: (member as any)?.in } : {}),
            }));

            try {
                const payload = {
                    data: {
                        person_ids: personIds.map((member) => member.id),
                    },
                    meta_ui: {
                        person_ids: personIds,
                    },
                };

                await updateProjectByPathService({
                    id: Number(projectId),
                    project_path: peopleSetPath,
                    data: payload,
                });

                if (peopleLonglistData) {
                    const updatedData = {
                        ...peopleLonglistData,
                        meta: { ...peopleLonglistData.meta, count: payload.data.person_ids.length },
                        data: { ...peopleLonglistData.data, person_ids: payload.data.person_ids },
                        meta_ui: payload.meta_ui,
                    };
                    dispatch(updatePersonSet(updatedData));
                }
            } catch (error) {
                dispatch(toggleLoader(false));
                console.error(error);
            }
        } else {
            // Creating
            try {
                const { data } = await saveProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/person_set_list`,
                    data: {
                        name: `Long list ` + `${new Date().getTime()}`.slice(-5),
                        type: "LONG_LIST",
                        person_ids: selectedPeople.map((member) => member.id),
                    },
                    echo: true,
                });

                const peopleSet = data.children.at(-1);
                const peopleSetId = peopleSet.data.id;

                if (!isEmpty(peopleSet)) {
                    dispatch(addPersonSet([peopleSet]));
                }

                const payload = {
                    data: {},
                    ref: {
                        path: `/project_${projectId}/person_set_list/person_set_${peopleSetId}`,
                    },
                };

                await updateProjectByPathService({
                    id: Number(projectId),
                    project_path: `/project_${projectId}/search_ai_list/search_ai_${searchAiId}/long_list`,
                    data: payload,
                });

                const searchLongListSetRef = getSearchAiLeadersLongList(searchAiData);

                if (searchLongListSetRef && searchAiId) {
                    const updatedData = {
                        ...searchLongListSetRef,
                        ref: payload.ref,
                    };

                    dispatch(
                        updateSearchAiChild({
                            data: updatedData,
                            id: Number(searchAiId),
                            type: "PROJECT_PERSON_SET_REF_A",
                        })
                    );
                }
            } catch (error) {
                dispatch(toggleLoader(false));
                console.error(error);
            }
        }

        dispatch(clearRefinementData());
        dispatch(clearLongList());
        dispatch(clearShortList());
        navigate(
            `${urlPaths.TalentRefinement}/${companyId}?projectId=${projectId}&insightId=${insightId}&searchAiId=${searchAiId}`
        );
    };

    const handleFilterCandidates = async () => {
        if (wasBriefModified && !briefChangesConfirmed && !isDumbSearch) {
            setShowBriefError(true);
            window.scrollTo({
                top: 0,
                behavior: "smooth",
            });
            return;
        }

        setPagination((currentPagination) => ({ ...currentPagination, page: 1 }));
        setPeople({
            meta: initialPaginationOS,
            results: [],
        });
        setSortingDirection("default");
        await updateSearch();
        handlePeopleFetch();
        setCollapse(false);
        setPageValue(1);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.value === "") {
            setPageValue("");
            return;
        }
        const inputValue = Number(event.target.value);
        if (inputValue > 0 && inputValue <= people?.meta.page_total) {
            previousPageValue.current = pageValue;
            setPageValue(inputValue);
        }
    };

    const handleOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            if (pageValue === people.meta.page) {
                return;
            }
            paginationTextInputRef.current.blur();
        }

        if (event.key === "Escape") {
            setPageValue(previousPageValue.current || 1);
            setTimeout(() => {
                paginationTextInputRef.current.blur();
            }, 1);
            return;
        }
    };

    const handleOnBlur = () => {
        if (pageValue === "" || pageValue === people.meta.page) {
            setPageValue(people.meta.page);
            return;
        }

        if (Boolean(pageValue) && Number(pageValue) > 0 && Number(pageValue) <= people.meta.page_total) {
            onPageChange(Number(pageValue));
        }
    };

    const onPageChange = (page: number) => {
        if (page < 1 || page > people.meta?.page_total) {
            return;
        }
        setPageValue(page);
        setMarkedLeft([]);
        handlePeopleFetch({ page, orderByColumn: pagination?.orderByColumn });
    };

    // This function removes all NON BRIEF related filters.
    const handleClearAllFilters = () => {
        setWHFilter(WHdata);
        setWHRelevanceFilter(WHRelevance);
        setJobTitleTerm("");
        setCity(defaultLocationData);
        setRadius(defaultCityRadius);
        setCountry(defaultCountryData);
    };

    const collapseHandler = () => {
        setCollapse(!collapse);
        if (!collapse) setShowCollapseExpand(false);
    };

    const collapseHandlerEnd = () => {
        if (!collapse) setShowCollapseExpand(true);
    };

    const handleCollapseCompanies = () => {
        setCollapseCompanies(!collapseCompanies);
    };

    const handleSaveChartChanges = async () => {
        handleConfirmChanges(true);
        await updateSearch();
        dispatch(toggleLoader(false));
    };

    // Fetch Project data and fill-in filters
    useEffect(() => {
        const initialFetch = async () => {
            dispatch(toggleLoader(true));

            // Check if there is a valid SLT
            const sltResult = await getInsightSlt();
            if (!sltResult) {
                setHasBoardMembers(sltResult);
                dispatch(toggleLoader(false));
                return;
            }

            const promises = [];
            let result = {} as TInitialFetch | TInitialFetchError;

            if (isEmpty(searchAiData) && projectId && searchAiId) {
                promises.push(
                    dispatch(
                        fetchProjectSearchAi({
                            projectId: Number(projectId),
                            path: `/project_${projectId}/search_ai_list/search_ai_${searchAiId}`,
                        })
                    )
                );
            }

            if (!functionsList.length && functionsAndHierarchiesStatus !== "fetching") {
                promises.push(dispatch(getFunctionsAndLevelsThunk()));
            }

            if (promises.length && projectId) {
                const response = await Promise.allSettled(promises);
                result = getInitialFetchResponse(response);

                if ("error" in result) {
                    console.error(result.reason);
                    dispatch(toggleLoader(false));
                    navigate(urlPaths.NotFound);
                    return;
                }
            }

            const searchAiResult = "searchAi" in result ? result.searchAi : searchAiData;
            const functionsResult = "functionsList" in result ? result.functionsList : functionsList;
            const hierarchiesResult = "hierarchiesList" in result ? result.hierarchiesList : hierarchiesList;

            const wasDataset = await setData(searchAiResult, functionsResult, hierarchiesResult);

            if (wasDataset) {
                setShouldFetchBrief(true);
            } else {
                dispatch(toggleLoader(false));
            }
        };

        const getInsightSlt = async () => {
            if (projectId === null) {
                return false;
            }

            let newInsight = insightData;

            if (isEmpty(insightData) && insightId) {
                newInsight = unwrapResult(
                    await dispatch(
                        getProjectInsight({
                            projectId,
                            path: `/project_${projectId}/insight_list/insight_${insightId}`,
                        })
                    )
                );
            }

            const sltPath = getInsightRelatedSltPath(newInsight)?.ref?.path;

            if (!sltPath) {
                return false;
            }

            const sltPeopleSet: ProjectPersonSet = unwrapResult(
                await dispatch(fetchPersonSet({ projectId, path: sltPath }))
            );

            return Boolean(sltPeopleSet.data.person_ids.length);
        };

        const setData = async (
            searchAiData: ProjectSearchAICreationResponse | undefined,
            functionsList: TElement[] | undefined,
            hierarchiesList: TElement[] | undefined
        ) => {
            if (!searchAiData || projectId === null) return;

            // Selected companies
            const companyLongListPath = getSearchAiCompaniesLonglist(searchAiData)?.ref.path;
            if (companyLongListPath) {
                const companyResult: ProjectCompanySet = unwrapResult(
                    await dispatch(fetchCompanySet({ projectId, path: companyLongListPath }))
                );
                const selectedCompaniesData = companyResult.children.map((company) => company.data);

                if (selectedCompaniesData.length) {
                    setSelectedCompanies(selectedCompaniesData.map(mapForChipEntity));
                }
            }

            // Filters
            const filters = getSearchAiLeadersFilters(searchAiData)?.data;

            if (filters && !isEmpty(filters)) {
                // Functions
                if (filters.role_functions.length) {
                    const selectedFunctions = filters.role_functions.flatMap((f) => {
                        const value = functionsList?.find((element) => element.type === f);
                        if (value) {
                            return {
                                key: value.id,
                                value: value.type ?? "",
                                title: value.name,
                                selected: true,
                            };
                        }
                        return [];
                    });

                    setFunctions(selectedFunctions);
                    wereFHUpdated.current = true;
                } else if (filters.role_function) {
                    const value = functionsList?.find((element) => element.type === filters.role_function);

                    if (value) {
                        setFunctions([
                            {
                                key: value.id,
                                value: value.type ?? "",
                                title: value.name,
                                selected: true,
                            },
                        ]);
                        wereFHUpdated.current = true;
                    }
                }

                // Levels
                if (filters.role_levels.length) {
                    const selectedHierarchies = filters.role_levels.flatMap((h) => {
                        const hierarchyValue = hierarchiesList?.find((element) => element.type === h);
                        if (hierarchyValue) {
                            return {
                                key: hierarchyValue.id,
                                value: hierarchyValue.type ?? "",
                                title: hierarchyValue.name,
                                selected: true,
                            };
                        }
                        return [];
                    });
                    setHierarchies(selectedHierarchies);
                    wereFHUpdated.current = true;
                } else if (filters.role_level) {
                    const value = hierarchiesList?.filter((element) => element.type === filters.role_level)[0];
                    if (value) {
                        setHierarchies([
                            {
                                key: value.id,
                                value: value.type ?? "",
                                title: value.name,
                                selected: true,
                            },
                        ]);
                        wereFHUpdated.current = true;
                    }
                }

                // Job Title
                if (filters.job_title) {
                    setJobTitleTerm(filters.job_title);
                }

                // Location
                if (filters.geo_radius && !isEmpty(filters.geo_radius)) {
                    fetchLocationData(filters.geo_radius);
                }

                // Work History Scope
                if (filters.work_history_scope) {
                    const advancedSearchWH = WHdata.map((wh) => ({
                        ...wh,
                        selected: wh.label.includes(filters.work_history_scope.toLowerCase()),
                    }));

                    const dumbSearchWH = WHRelevance.map((wh, i) => {
                        const isHistoric = filters.work_history_scope.toLowerCase() === "historic";
                        return {
                            ...wh,
                            selected:
                                i === 0 && isHistoric
                                    ? true
                                    : wh.label.includes(filters.work_history_scope.toLowerCase()),
                        };
                    });

                    setWHFilter(advancedSearchWH);
                    setWHRelevanceFilter(dumbSearchWH);
                }

                // Narratives
                if (filters.lspm_scores.length) {
                    setNarrativesArr(
                        (currentNarratives) =>
                            filters.lspm_scores.map((lc) => ({
                                id: lc.narrative.toLowerCase(),
                                label: snakeCaseToCapitalizedString(lc.narrative),
                                ...(lc.score_range.min ? { min: lc.score_range.min } : {}),
                                ...(lc.score_range.max ? { max: lc.score_range.max } : {}),
                                ...(lc.score_range.min !== null || lc.score_range.max !== null
                                    ? {
                                          checkboxes: {
                                              minimum: typeof lc.score_range.min === "number",
                                              maximum: typeof lc.score_range.max === "number",
                                          },
                                      }
                                    : {}),
                            })) ?? currentNarratives
                    );
                }
            }

            // People Lists (ShortList and LongList)
            const promises = [];
            const longListSetPath = getSearchAiLeadersLongList(searchAiData)?.ref?.path;
            const shortListSetPath = getSearchAiLeadersShortList(searchAiData)?.ref?.path;

            if (longListSetPath) {
                setPeopleLonglistPath(longListSetPath);
                promises.push(dispatch(fetchPersonSet({ projectId, path: longListSetPath })));
            }

            if (shortListSetPath) {
                promises.push(dispatch(fetchPersonSet({ projectId, path: shortListSetPath })));
            }

            const response = await Promise.allSettled(promises);
            const result = getListsFetchResponse<ProjectPersonSet>(response);

            if ("error" in result) {
                isLoaderVisible && dispatch(toggleLoader(false));
                console.error(result.reason);
                navigate(urlPaths.NotFound);
                return;
            }

            const [peopleSetLongList, peopleSetShortList] = result;

            // LongList
            const longListSet = peopleSetLongList?.data.person_ids || [];
            const longListMetaUiList = peopleSetLongList?.meta_ui?.person_ids;
            const peopleLongList =
                longListMetaUiList?.filter((person) => person.in !== "notInterested").map((person) => person.id) || [];

            // ShortList
            const shortListSet = peopleSetShortList?.data.person_ids || [];
            const notInterestedListSet =
                longListMetaUiList?.filter((person) => person.in === "notInterested").map((person) => person.id) || [];

            const shortList = [...shortListSet, ...notInterestedListSet];
            let longList = [];

            if (peopleLongList.length) {
                longList = difference(longListSet, shortList).filter((person) => peopleLongList.includes(person));
            } else {
                longList = difference(longListSet, shortList);
            }

            // Get Shortlist and Not Interested People lists
            if (shortList.length) {
                await fetchPeopleData(shortList, "shortlist");
            }

            // Get Longlist
            if (longList.length) {
                await fetchPeopleData(longList, "longlist");
            }

            return true;
        };

        const fetchLocationData = async (locationData: any) => {
            try {
                if (locationData.country_id) {
                    const { data } = await fetchCountryByIdService<ILocationCountryById>(locationData.country_id);

                    data &&
                        setCountry({
                            id: data.country_id,
                            name: data.country_record.name,
                            code_iso_alpha2: data.country_record.code_iso_alpha2,
                            code_iso_alpha3: data.country_record.code_iso_alpha3,
                        });
                }

                if (locationData.city_id) {
                    const { data } = await fetchLocationByIdService<ILocationDataById>(locationData.city_id);

                    data &&
                        setCity({
                            id: data.location_id,
                            city: data.location_record.city,
                            country_id: data.location_record.country_id,
                            country: data.location_record.country,
                            location: data.location_record.location,
                        });
                }
            } catch (error) {
                console.error(error);
            }

            if (locationData.radius) {
                setRadius(locationData.radius);
            }
        };

        const fetchPeopleData = async (peopleIds: number[], type: "shortlist" | "longlist") => {
            try {
                const peopleWithData = await requestPeopleData(peopleIds, talentPotentialMembersPayloadColumns);
                if (type === "longlist") {
                    setSelectedPeople(peopleWithData);
                }
                if (type === "shortlist") {
                    setSelectedShortList(peopleWithData);
                }
            } catch (error) {
                console.error(error);
                dispatch(toggleLoader(false));
            }
        };

        initialFetch();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update Function and Hierarchy from Brief Data, if there is no data in SearchAI filters
    useEffect(() => {
        if (!wereFHUpdated.current && !isEmpty(briefData) && !functions.length && !hierarchies.length) {
            wereFHUpdated.current = true;

            const functionsValues = briefData.roleFunctions.flatMap((fn) => {
                const functionValue = functionsList.find((element) => element.type === fn);

                if (functionValue) {
                    return {
                        key: functionValue.id,
                        value: functionValue.type ?? "",
                        title: functionValue.name,
                        selected: true,
                    };
                }

                return [];
            });

            setFunctions(functionsValues);

            const hierarchiesValue = briefData.roleLevels.flatMap((level) => {
                const hierarchyValue = hierarchiesList.find((element) => element.type === level);

                if (hierarchyValue) {
                    return {
                        key: hierarchyValue.id,
                        value: hierarchyValue.type ?? "",
                        title: hierarchyValue.name,
                        selected: true,
                    };
                }
                return [];
            });

            setHierarchies(hierarchiesValue);
        }
    }, [briefData, functions, functionsList, hierarchies, hierarchiesList]);

    return (
        <ErrorBoundary FallbackComponent={GenericError}>
            <Box ref={containerRef}>
                <TalentHeader
                    title={intl.formatMessage({ id: "talent.people.title" })}
                    actions={
                        <Box>
                            <NavButton isBackButton onClick={handleGoBack} translationId={"talent.companies.title"} />
                            <NavButton
                                translationId="button.analyse.candidates"
                                onClick={handleSaveLeaders}
                                disabled={!selectedPeople.length}
                            />
                        </Box>
                    }
                >
                    <Box className={classes.filtersWrapper}>
                        {!collapse && showCollapseExpand && (
                            <IconButton className={classes.filtersExpanderButton} onClick={collapseHandler}>
                                <ExpandCircleDownIcon sx={{ color: "black" }} />
                            </IconButton>
                        )}

                        {/* Filters */}
                        <Collapse in={collapse} className={classes.collapse} onExited={collapseHandlerEnd}>
                            {/* Brief confirmation modal */}
                            {showConfirmBriefChanges && (
                                <CardMessageWrapper sx={{ backdropFilter: "blur(5px)" }}>
                                    <CardMessage
                                        sx={{ maxWidth: "500px !important" }}
                                        content={
                                            <>
                                                <Box className={classes.filterTitle} mb={2}>
                                                    <CustomIcon
                                                        className={classes.filterTitleIcon}
                                                        sx={{ color: "primary.main", mr: 0.5 }}
                                                        icon={icons.errorTriangle}
                                                    />
                                                    <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                                                        Are you sure?
                                                    </Typography>
                                                </Box>
                                                <Typography>
                                                    {intl.formatMessage({
                                                        id: "talent.people.filters.briefs.confiramtionMesssage",
                                                    })}
                                                </Typography>
                                            </>
                                        }
                                        actions={[
                                            {
                                                label: "Dismiss",
                                                onClickAction: () => handleConfirmChanges(false),
                                                buttonProps: {
                                                    variant: "outlined",
                                                },
                                            },
                                            {
                                                label: "Confirm",
                                                onClickAction: () => handleSaveChartChanges(),
                                            },
                                        ]}
                                    />
                                </CardMessageWrapper>
                            )}

                            <Typography paragraph className={classes.headerNarrative}>
                                {intl.formatMessage({ id: "talent.people.filters.headerNarrative" })}
                            </Typography>
                            <Box sx={{ mb: 4 }}>
                                <Box className={classes.wrapperSubtitleLinks}>
                                    <TitleFilter
                                        showCrossLine
                                        title={intl.formatMessage({ id: "talent.people.filters.subtitle" })}
                                        icon={<CustomIcon icon={icons.filter} />}
                                    />
                                    <Box sx={{ flex: 1, height: "1px", backgroundColor: "#DADEE6", margin: "auto" }} />
                                    <Box className={classes.tabsContainer}>
                                        <Link
                                            className={`${classes.tabButton} ${
                                                activeButton === 0 && classes.activeTabButton
                                            }`}
                                            onClick={() => handleTabChange(0)}
                                        >
                                            <Typography>
                                                {intl.formatMessage({ id: "talent.people.filters.basicSearch" })}
                                            </Typography>
                                        </Link>
                                        <Link
                                            className={`${classes.tabButton} ${
                                                activeButton === 1 && classes.activeTabButton
                                            }`}
                                            onClick={() => handleTabChange(1)}
                                        >
                                            <Typography>
                                                {intl.formatMessage({ id: "talent.people.filters.advancedSearch" })}
                                            </Typography>
                                        </Link>
                                    </Box>
                                </Box>

                                <Typography paragraph>
                                    {intl.formatMessage({ id: "talent.people.filters.subtitleNarrative" })}
                                </Typography>
                            </Box>

                            {/* Selected Companies */}
                            <Box
                                className={cn(
                                    classes.filterWrapper,
                                    classes.filterWrapperGreen,
                                    classes.filterWrapperShadow
                                )}
                            >
                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                    <Box className={classes.filterSectionTitle}>
                                        <CustomIcon icon={icons.peer} />
                                        <Typography>
                                            {intl.formatMessage({ id: "talent.people.filters.companies" })}
                                        </Typography>
                                    </Box>
                                    <IconButton onClick={handleCollapseCompanies}>
                                        <CustomIcon
                                            type="outlined"
                                            sx={{ fontSize: 16 }}
                                            icon={collapseCompanies ? icons.chevronDownThin : icons.chevronUpThin}
                                        />
                                    </IconButton>
                                </Box>
                                <Collapse in={!collapseCompanies}>
                                    <Typography paragraph className={classes.narratives}>
                                        {intl.formatMessage({ id: "talent.people.filters.companies.narrative" })}
                                    </Typography>
                                    <ChipsArray colorChip="secondary" chipData={selectedCompanies} />
                                </Collapse>
                            </Box>

                            {/* Selected Brief */}
                            {!isDumbSearch && hasBoardMembers && (
                                <Box className={cn(classes.filterWrapper, classes.filterWrapperBlue)}>
                                    {showBriefError && (
                                        <Alert
                                            iconMapping={{ warning: <></> }}
                                            variant="filled"
                                            severity="warning"
                                            sx={{ mb: 3, ".MuiAlert-message": { width: "100%" } }}
                                            action={<></>}
                                        >
                                            <Box display="flex" alignItems="center" justifyContent="center">
                                                <CustomIcon icon={icons.errorTriangle} sx={{ mr: 1, fontSize: 18 }} />
                                                <Typography sx={{ fontSize: 16 }}>
                                                    {intl.formatMessage({
                                                        id: "talent.people.filters.briefsAlertMessage",
                                                    })}
                                                </Typography>
                                            </Box>
                                        </Alert>
                                    )}
                                    <Box className={classes.filterSectionTitle} mb={1.5}>
                                        <CustomIcon icon={icons.documentLightBulb} />
                                        <Typography variant="h6">
                                            {intl.formatMessage({ id: "talent.people.filters.briefsSubtitle" })}
                                        </Typography>
                                    </Box>
                                    <Typography paragraph>
                                        {intl.formatMessage({ id: "talent.people.filters.briefsDescription" })}
                                    </Typography>

                                    <Box mb={2.5}>
                                        <SelectAndChips
                                            type="function"
                                            label={intl.formatMessage({ id: "people.function" })}
                                            icon={<CustomIcon className={classes.filterTitleIcon} icon={icons.users} />}
                                            description={intl.formatMessage({
                                                id: "talent.people.filters.function.narrative",
                                            })}
                                            list={functionsList}
                                            items={functions}
                                            setItems={setFunctions}
                                            fieldPlaceholder={placeholderFunctions}
                                        />
                                    </Box>

                                    <Box mb={2.5}>
                                        <SelectAndChips
                                            type="hierarchy"
                                            label={intl.formatMessage({ id: "people.hierarchy" })}
                                            icon={<CustomIcon className={classes.filterTitleIcon} icon={icons.users} />}
                                            description={intl.formatMessage({
                                                id: "talent.people.filters.hierarchy.narrative",
                                            })}
                                            list={hierarchiesList}
                                            items={hierarchies}
                                            setItems={setHierarchies}
                                            fieldPlaceholder={placeholderHierarchy}
                                        />
                                    </Box>

                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Box className={classes.filterTitle}>
                                            <CustomIcon className={classes.filterTitleIcon} icon={icons.crossHair} />
                                            <Typography className={classes.filterTitleLabel}>
                                                LSPM Chart Location
                                            </Typography>
                                        </Box>
                                        <Button variant="outlined" size="small" onClick={handleOpenSnackBar}>
                                            <CustomIcon sx={{ mr: 1, fontSize: 16 }} icon={icons.eye} />
                                            <Typography sx={{ fontSize: 12 }}>View Experience Weightings</Typography>
                                        </Button>
                                    </Box>
                                    <Box ref={snackBarWrapperRef}>
                                        <SnackBar
                                            maxWidth={width * 2.15 || 400}
                                            shouldOpen={shouldOpenSnackBar}
                                            anchorElement={popOverAnchorEl}
                                            title={
                                                <Box className={classes.filterTitle}>
                                                    <CustomIcon
                                                        className={classes.filterTitleIcon}
                                                        icon={icons.equalizer}
                                                    />
                                                    <Typography className={classes.filterTitleLabel}>
                                                        Experiences
                                                    </Typography>
                                                </Box>
                                            }
                                            handleInfoClose={handleCloseSnackBar}
                                            transformOrigin={{
                                                vertical: "top",
                                                horizontal: "center",
                                            }}
                                            {...(snackBarWrapperRef.current
                                                ? {
                                                      anchorPosition: {
                                                          top:
                                                              snackBarWrapperRef.current?.getBoundingClientRect()
                                                                  .bottom - 40,
                                                          left:
                                                              snackBarWrapperRef.current?.getBoundingClientRect().left +
                                                              snackBarWrapperRef.current?.getBoundingClientRect()
                                                                  .width /
                                                                  2,
                                                      },
                                                  }
                                                : {})}
                                            content={
                                                <>
                                                    <Typography paragraph>Experiences set in create brief</Typography>
                                                    <Box sx={{ backgroundColor: "white", p: 2, borderRadius: 1 }}>
                                                        <BriefFacets
                                                            isReadOnly
                                                            hideDescription
                                                            briefData={briefData}
                                                            setBriefData={handleSetBriefData}
                                                        />
                                                    </Box>
                                                </>
                                            }
                                        />
                                    </Box>

                                    <Typography paragraph>
                                        {intl.formatMessage({ id: "talent.people.filters.chartsDescription" })}
                                    </Typography>
                                    <Box className={classes.briefChartsWrapper}>
                                        <Box ref={briefChartsWrapperRef}>
                                            <Typography className={classes.filterTitleLabel}>
                                                Functional Balance
                                            </Typography>
                                            <Typography paragraph>
                                                This analyses an individual&apos;s experience within their functional
                                                background.
                                            </Typography>
                                            {chartStatus === "done" ? (
                                                <>
                                                    <Box className={classes.chartWrapper}>
                                                        <Balance
                                                            width={width}
                                                            height={height}
                                                            getData={getLD5}
                                                            getbullEyeCoors={(data) =>
                                                                handleUpdateBriefData(data, "chart1")
                                                            }
                                                            moreOptions={{
                                                                hideLegend: true,
                                                                hideCustomizeLabels: true,
                                                                bullEyeCoors: briefData?.chart1,
                                                                elementsToDraw: [briefDataStore?.chart1],
                                                                exporting: false,
                                                                disableZoom: true,
                                                                disableSeriesClick: true,
                                                            }}
                                                        />
                                                    </Box>
                                                    <InputSlider
                                                        range={[1, 100]}
                                                        value={briefData?.["chart1"]?.radius || 1}
                                                        getValue={(value) =>
                                                            handleUpdateBriefData({ radius: value }, "chart1")
                                                        }
                                                    />
                                                </>
                                            ) : (
                                                <Skeleton variant="rectangular" animation="wave" height={400} />
                                            )}
                                        </Box>
                                        <Box>
                                            <Typography className={classes.filterTitleLabel}>
                                                Individual Positioning
                                            </Typography>
                                            <Typography paragraph>
                                                This analyses an individual&apos;s experience of domain against
                                                situational.
                                            </Typography>
                                            {chartStatus === "done" ? (
                                                <>
                                                    <Box className={classes.chartWrapper}>
                                                        <TeamCompetitivePositioning
                                                            width={width}
                                                            height={height}
                                                            getData={getLD4}
                                                            getbullEyeCoors={(data) =>
                                                                handleUpdateBriefData(data, "chart2")
                                                            }
                                                            moreOptions={{
                                                                hideLegend: true,
                                                                hideCustomizeLabels: true,
                                                                bullEyeCoors: briefData?.chart2,
                                                                elementsToDraw: [briefDataStore?.chart2],
                                                                exporting: false,
                                                                disableZoom: true,
                                                                disableSeriesClick: true,
                                                            }}
                                                        />
                                                    </Box>
                                                    <InputSlider
                                                        range={[1, 100]}
                                                        value={briefData?.["chart2"]?.radius || 1}
                                                        getValue={(value) =>
                                                            handleUpdateBriefData({ radius: value }, "chart2")
                                                        }
                                                    />
                                                </>
                                            ) : (
                                                <Skeleton variant="rectangular" animation="wave" height={400} />
                                            )}
                                        </Box>
                                    </Box>
                                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                                        <Button
                                            disabled={!wasBriefModified}
                                            variant="outlined"
                                            size="small"
                                            onClick={handleRevertChanges}
                                        >
                                            <Typography sx={{ fontSize: 12 }}>Revert to original</Typography>
                                        </Button>
                                        <Button
                                            disabled={!wasBriefModified || briefChangesConfirmed}
                                            variant="contained"
                                            size="small"
                                            sx={{ ml: 1 }}
                                            onClick={handleShowConfirmationModal}
                                        >
                                            <Typography sx={{ fontSize: 12 }}>Save Changes</Typography>
                                        </Button>
                                    </Box>
                                </Box>
                            )}

                            {/* WH Relevance */}
                            {isDumbSearch && (
                                <Box className={cn(classes.filterWrapper, classes.filterWrapperGrey)}>
                                    <WHFilter
                                        data={whRelevanceFilter}
                                        setData={setWHRelevanceFilter}
                                        title={intl.formatMessage({ id: "talent.people.wh.relevance" })}
                                    />
                                </Box>
                            )}

                            {/* WHFilter */}
                            {!isDumbSearch && (
                                <Box className={cn(classes.filterWrapper, classes.filterWrapperGrey)}>
                                    <WHFilter
                                        data={whFilter}
                                        setData={setWHFilter}
                                        title={intl.formatMessage({ id: "talent.people.wh" })}
                                    />
                                </Box>
                            )}

                            {/* Functions */}
                            {/* {!isDumbSearch ? (
                                <Box className={cn(classes.filterWrapper, classes.filterWrapperGrey)}>
                                    <SelectAndChips rolesList={groupedRoles} roles={roles} setFunctions={setFunctions} />
                                </Box>
                            ) : null} */}

                            {/* Job Title */}
                            <Box className={cn(classes.filterWrapper, classes.filterWrapperGrey)}>
                                <JobTitle term={jobTitleTerm} setTerm={setJobTitleTerm} />
                            </Box>

                            {/* Country/City/Radius */}
                            {!isDumbSearch && (
                                <Box className={cn(classes.filterWrapper, classes.filterWrapperGrey)}>
                                    <CountryCity
                                        city={city}
                                        radius={radius}
                                        country={country}
                                        setCity={setCity}
                                        setRadius={setRadius}
                                        setCountry={setCountry}
                                    />
                                </Box>
                            )}

                            {/* LSPM filter */}
                            {/* {!isDumbSearch && (
                                <Box className={cn(classes.filterWrapper, classes.filterWrapperGrey)}>
                                    <LSPMFilter lspmNarratives={narrativesArr} setLspmNarratives={setNarrativesArr} />
                                </Box>
                            )} */}

                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Button
                                    variant="contained"
                                    className={classes.filterButton}
                                    onClick={handleFilterCandidates}
                                    startIcon={<CustomIcon icon={icons.filter} />}
                                    disabled={jobTitleTerm ? jobTitleTerm.length < 3 : false}
                                >
                                    {/* Filter Candidates */}
                                    <Typography variant="body2">
                                        {intl.formatMessage({ id: "talent.people.filters.button" })}
                                    </Typography>
                                </Button>
                                <ClearFilterButton
                                    onClick={handleClearAllFilters}
                                    disabled={!isFiltersModified}
                                    sx={{
                                        fontSize: 13,
                                        "& .MuiButton-startIcon>*:nth-of-type(1)": {
                                            fontSize: 16,
                                        },
                                    }}
                                    startIcon={<CustomIcon icon={icons.circledTimesHollowed} />}
                                />
                            </Box>

                            <IconButton className={classes.filtersExpanderButton} onClick={collapseHandler}>
                                <ExpandCircleDownIcon sx={{ transform: "rotate(180deg)", color: "black" }} />
                            </IconButton>
                        </Collapse>
                    </Box>
                </TalentHeader>
                <TalentBody>
                    <Grid container>
                        <Typography paragraph className={classes.explanation}>
                            {intl.formatMessage({ id: "talent.table.people.explanation" })}
                        </Typography>
                        <Grid item className={classes.talentTableSource}>
                            <TalentTableToolbar
                                numResults={people.meta?.results_total ?? 0}
                                type="people"
                                disabled={!Boolean(markedLeft.length)}
                                onMarked={handleAddBulk}
                                action="add"
                                isBasicSearch={isDumbSearch}
                                pagination={
                                    <TablePagination
                                        currentPage={people.meta.page}
                                        totalPages={people.meta.page_total}
                                        pageValue={pageValue}
                                        pageSize={people.meta.page_size}
                                        totalHits={people.meta.results_total}
                                        onPageChange={onPageChange}
                                        handleInputChange={handleInputChange}
                                        handleOnBlur={handleOnBlur}
                                        handleOnKeyDown={handleOnKeyDown}
                                        inputRef={paginationTextInputRef}
                                        isSearchAIPagination
                                    />
                                }
                            />
                        </Grid>
                        <Grid item className={classes.talentTableSelected}>
                            <TalentTableToolbar
                                unSelectables={selectedShortList}
                                numResults={selectedPeople.length}
                                type="people"
                                disabled={!Boolean(markedRight.length)}
                                onMarked={handleDeleteBulk}
                                action="delete"
                                search={
                                    <CustomAutocomplete
                                        id="talent-search-people"
                                        placeholder={placeholderPerson}
                                        options={peopleList}
                                        suggestions={suggestedPeopleLists}
                                        setSuggestions={setSuggestedPeopleLists}
                                        currentPage={currentPage}
                                        totalPages={totalPages}
                                        pageSize={pageSize}
                                        resultsRange={resultsRange}
                                        handlePageChange={handlePageChange}
                                        openOnlyOnOptionsLoaded
                                        getOptionLabel={getOptionLabelPerson}
                                        onInputChangeCallback={handleInputChangePerson}
                                        optionsUpdater={setPeopleList}
                                        filterOptions={(option) => option}
                                        onSuggestionSelectCallback={handlePersonSelect}
                                        onOptionSelectCallback={handlePersonSelect}
                                        showPreviousSearchBtn={showPreviousSearchBtn}
                                        setShowPreviousSearchBtn={setShowPreviousSearchBtn}
                                        searchRequestStatus={searchRequestStatus}
                                        resetSearch={resetStates}
                                        handleListsOrder={handleListsOrder}
                                        handleBackToPreviousResults={handleBackToPreviousResults}
                                        handleFetchSuggestedList={handleFetchSuggestedList}
                                        handleUpdateSuggestedList={handleUpdateSuggestedList}
                                        shouldShowLoadingState={shouldShowLoadingState}
                                    />
                                }
                            />
                        </Grid>
                        <Grid item className={cn(classes.talentTableSource, classes.talentTableSourceSx)}>
                            <TalentTable
                                type="people"
                                data={people.results}
                                selectedElements={selectedPeople}
                                setSelectedElements={setSelectedPeople}
                                setMarked={setMarkedLeft}
                                marked={markedLeft}
                                onClickRow={handleClickRow}
                                isBasicSearch={isDumbSearch}
                                isLongList={{
                                    value: false,
                                    onSelectAllAdd: handleAddSelectAllClick,
                                    onAddRow: handleAddRow,
                                }}
                                onRequestSort={onRequestSort}
                                containerRef={containerRef}
                                showPopover={showPopover}
                                setShowPopover={setShowPopover}
                                popoverPeople={personInfo}
                                sortingDirection={sortingDirection}
                                setSortingDirection={setSortingDirection}
                            />
                        </Grid>
                        <Grid item className={cn(classes.talentTableSelected, classes.talentTableSelectedSx)}>
                            <TalentTable
                                hideColumnsSubmenu
                                containsSelectOptions
                                type="people"
                                columns={["name"]}
                                data={selectedPeople}
                                selectedElements={selectedPeople}
                                setSelectedElements={setSelectedPeople}
                                setMarked={setMarkedRight}
                                marked={markedRight}
                                onClickRow={handleClickRow}
                                isLongList={{
                                    value: true,
                                    onSelectAllDelete: handleDeleteSelectAllClick,
                                    onDeleteRow: handleDeleteRow,
                                }}
                                onSort={onSort}
                                containerRef={containerRef}
                                showPopover={showPopover}
                                setShowPopover={setShowPopover}
                                popoverPeople={personInfo}
                            />
                        </Grid>
                    </Grid>
                </TalentBody>
                <Modal open={!hasBoardMembers}>
                    <Box
                        sx={{
                            width: "100%",
                            height: "100%",
                            justifyContent: "center",
                            alignItems: "center",
                            display: "flex",
                            position: "fixed",
                            backgroundColor: "rgb(255 255 255 / 80%)",
                            top: 0,
                            zIndex: 3,
                        }}
                    >
                        <ChartErrorMessage />
                    </Box>
                </Modal>
            </Box>
        </ErrorBoundary>
    );
};

export default People;
